import React, { useState, useEffect } from 'react';
import { performanceApi, osApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DonutChart, SimpleBarChart } from '../components/charts';
import {
  Loader2,
  TrendingUp,
  TrendingDown,
  Clock,
  Users,
  FileText,
  AlertTriangle,
  Award,
  ChevronLeft,
  ChevronRight,
  Plus,
  BarChart3,
  Target,
  Zap,
  AlertCircle,
  CheckCircle,
  XCircle,
  Info,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';

const PerformancePage = () => {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [osList, setOsList] = useState([]);
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [showAddOSDialog, setShowAddOSDialog] = useState(false);
  const [newOS, setNewOS] = useState({
    numero: '',
    cliente: '',
    local: '',
    data_evento: '',
    data_montagem: '',
    data_desmontagem: '',
    horas_previstas: { montagem: 0, evento: 0, desmontagem: 0, outros: 0 },
    valor_contrato: 0,
    observacoes: '',
    status: 'aberta'
  });
  const [savingOS, setSavingOS] = useState(false);
  const [termosData, setTermosData] = useState(null);

  const meses = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' },
  ];

  useEffect(() => {
    loadData();
  }, [mes, ano]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [reportRes, osRes, termosRes] = await Promise.all([
        performanceApi.getReport(mes, ano),
        osApi.list({ mes, ano }),
        performanceApi.getTermos(mes, ano)
      ]);
      setReportData(reportRes.data);
      setOsList(osRes.data || []);
      setTermosData(termosRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      if (error.response?.status !== 404) {
        toast.error('Erro ao carregar relatório de performance');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousMonth = () => {
    if (mes === 1) {
      setMes(12);
      setAno(ano - 1);
    } else {
      setMes(mes - 1);
    }
  };

  const handleNextMonth = () => {
    if (mes === 12) {
      setMes(1);
      setAno(ano + 1);
    } else {
      setMes(mes + 1);
    }
  };

  const handleAddOS = async () => {
    if (!newOS.numero || !newOS.cliente || !newOS.data_evento) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    setSavingOS(true);
    try {
      await osApi.create(newOS);
      toast.success('OS cadastrada com sucesso');
      setShowAddOSDialog(false);
      setNewOS({
        numero: '',
        cliente: '',
        local: '',
        data_evento: '',
        data_montagem: '',
        data_desmontagem: '',
        horas_previstas: { montagem: 0, evento: 0, desmontagem: 0, outros: 0 },
        valor_contrato: 0,
        observacoes: '',
        status: 'aberta'
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar OS');
    } finally {
      setSavingOS(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'otimizado': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'dentro_previsto': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'excedido': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'otimizado': return 'Otimizado';
      case 'dentro_previsto': return 'Dentro do Previsto';
      case 'excedido': return 'Excedido';
      case 'sem_previsao': return 'Sem Previsão';
      default: return status;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 dark:text-green-400';
    if (score >= 70) return 'text-blue-600 dark:text-blue-400';
    if (score >= 50) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  };

  const handleExportTermosCsv = async () => {
    try {
      const response = await performanceApi.exportTermosCsv(mes, ano);
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const mesNome = meses.find(m => m.value === mes)?.label || mes;
      link.setAttribute('download', `termos_${mesNome}_${ano}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar CSV:', error);
      toast.error('Erro ao exportar CSV');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="performance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
            Relatório de Performance
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Análise inteligente: Horas Previstas vs Apontadas
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={handlePreviousMonth}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          <Select value={String(mes)} onValueChange={(v) => setMes(Number(v))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {meses.map((m) => (
                <SelectItem key={m.value} value={String(m.value)}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={String(ano)} onValueChange={(v) => setAno(Number(v))}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map((a) => (
                <SelectItem key={a} value={String(a)}>
                  {a}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" size="icon" onClick={handleNextMonth}>
            <ChevronRight className="w-4 h-4" />
          </Button>

          <Dialog open={showAddOSDialog} onOpenChange={setShowAddOSDialog}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Nova OS
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Cadastrar Ordem de Serviço</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Número da OS *</Label>
                    <Input
                      placeholder="Ex: OS-2025-001"
                      value={newOS.numero}
                      onChange={(e) => setNewOS({ ...newOS, numero: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={newOS.status} onValueChange={(v) => setNewOS({ ...newOS, status: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aberta">Aberta</SelectItem>
                        <SelectItem value="em_andamento">Em Andamento</SelectItem>
                        <SelectItem value="fechada">Fechada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Cliente *</Label>
                  <Input
                    placeholder="Nome do cliente"
                    value={newOS.cliente}
                    onChange={(e) => setNewOS({ ...newOS, cliente: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Local</Label>
                  <Input
                    placeholder="Local do evento"
                    value={newOS.local}
                    onChange={(e) => setNewOS({ ...newOS, local: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Data Montagem</Label>
                    <Input
                      type="date"
                      value={newOS.data_montagem}
                      onChange={(e) => setNewOS({ ...newOS, data_montagem: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Data Evento *</Label>
                    <Input
                      type="date"
                      value={newOS.data_evento}
                      onChange={(e) => setNewOS({ ...newOS, data_evento: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Data Desmontagem</Label>
                    <Input
                      type="date"
                      value={newOS.data_desmontagem}
                      onChange={(e) => setNewOS({ ...newOS, data_desmontagem: e.target.value })}
                    />
                  </div>
                </div>

                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <Label className="text-sm font-semibold mb-3 block">Horas Previstas</Label>
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs">Montagem</Label>
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        value={newOS.horas_previstas.montagem}
                        onChange={(e) => setNewOS({
                          ...newOS,
                          horas_previstas: { ...newOS.horas_previstas, montagem: parseFloat(e.target.value) || 0 }
                        })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Evento</Label>
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        value={newOS.horas_previstas.evento}
                        onChange={(e) => setNewOS({
                          ...newOS,
                          horas_previstas: { ...newOS.horas_previstas, evento: parseFloat(e.target.value) || 0 }
                        })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Desmontagem</Label>
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        value={newOS.horas_previstas.desmontagem}
                        onChange={(e) => setNewOS({
                          ...newOS,
                          horas_previstas: { ...newOS.horas_previstas, desmontagem: parseFloat(e.target.value) || 0 }
                        })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Outros</Label>
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        value={newOS.horas_previstas.outros}
                        onChange={(e) => setNewOS({
                          ...newOS,
                          horas_previstas: { ...newOS.horas_previstas, outros: parseFloat(e.target.value) || 0 }
                        })}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Total Previsto: {(newOS.horas_previstas.montagem + newOS.horas_previstas.evento + newOS.horas_previstas.desmontagem + newOS.horas_previstas.outros).toFixed(1)}h
                  </p>
                </div>

                <div>
                  <Label>Valor do Contrato (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newOS.valor_contrato}
                    onChange={(e) => setNewOS({ ...newOS, valor_contrato: parseFloat(e.target.value) || 0 })}
                  />
                </div>

                <div>
                  <Label>Observações</Label>
                  <Input
                    placeholder="Observações gerais"
                    value={newOS.observacoes}
                    onChange={(e) => setNewOS({ ...newOS, observacoes: e.target.value })}
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowAddOSDialog(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleAddOS} disabled={savingOS}>
                    {savingOS ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Cadastrar OS
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Cards de Resumo */}
      {reportData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="dark:bg-slate-800/50 dark:border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Total de OS</p>
                  <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                    {reportData.resumo.total_os}
                  </p>
                </div>
                <FileText className="w-8 h-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="dark:bg-slate-800/50 dark:border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Horas Previstas</p>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {reportData.resumo.total_horas_previstas}h
                  </p>
                </div>
                <Target className="w-8 h-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="dark:bg-slate-800/50 dark:border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Horas Apontadas</p>
                  <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                    {reportData.resumo.total_horas_apontadas}h
                  </p>
                </div>
                <Clock className="w-8 h-8 text-amber-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className={`dark:bg-slate-800/50 dark:border-slate-700 ${
            reportData.resumo.diferenca_total > 0 ? 'border-red-200 dark:border-red-800' : 'border-green-200 dark:border-green-800'
          }`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Diferença</p>
                  <p className={`text-2xl font-bold ${
                    reportData.resumo.diferenca_total > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                  }`}>
                    {reportData.resumo.diferenca_total > 0 ? '+' : ''}{reportData.resumo.diferenca_total}h
                  </p>
                  <p className="text-xs text-slate-500">
                    ({reportData.resumo.percentual_geral > 0 ? '+' : ''}{reportData.resumo.percentual_geral}%)
                  </p>
                </div>
                {reportData.resumo.diferenca_total > 0 ? (
                  <TrendingUp className="w-8 h-8 text-red-500 opacity-50" />
                ) : (
                  <TrendingDown className="w-8 h-8 text-green-500 opacity-50" />
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Card de Média CLT */}
      {reportData && reportData.resumo.total_funcionarios_clt > 0 && (
        <Card className="border-blue-200 dark:border-blue-800 dark:bg-slate-800/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Média CLT ({reportData.resumo.total_funcionarios_clt} funcionários)
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Total: {reportData.resumo.total_horas_clt}h apontadas
                  </p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="text-center">
                  <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                    {reportData.resumo.media_horas_clt_por_pessoa}h
                  </p>
                  <p className="text-xs text-slate-500">por pessoa/mês</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-cyan-600 dark:text-cyan-400">
                    {reportData.resumo.media_horas_clt_por_dia}h
                  </p>
                  <p className="text-xs text-slate-500">por dia</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cards de Status das OS */}
      {reportData && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="border-green-200 dark:border-green-800 dark:bg-slate-800/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">OS Otimizadas</p>
                  <p className="text-xl font-bold text-green-600 dark:text-green-400">
                    {reportData.resumo.os_otimizadas}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-amber-200 dark:border-amber-800 dark:bg-slate-800/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Com Divergência</p>
                  <p className="text-xl font-bold text-amber-600 dark:text-amber-400">
                    {reportData.resumo.os_com_divergencia}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-200 dark:border-red-800 dark:bg-slate-800/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">OS Excedidas</p>
                  <p className="text-xl font-bold text-red-600 dark:text-red-400">
                    {reportData.resumo.os_excedidas}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs de Detalhes */}
      <Tabs defaultValue="os" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-4">
          <TabsTrigger value="os" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Por OS</span>
          </TabsTrigger>
          <TabsTrigger value="ranking" className="flex items-center gap-2">
            <Award className="w-4 h-4" />
            <span className="hidden sm:inline">Ranking</span>
          </TabsTrigger>
          <TabsTrigger value="termos" className="flex items-center gap-2">
            <XCircle className="w-4 h-4" />
            <span className="hidden sm:inline">Ajustes</span>
            {termosData?.resumo?.total_termos > 0 && (
              <Badge variant="destructive" className="ml-1 text-xs">
                {termosData.resumo.total_termos}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="alertas" className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            <span className="hidden sm:inline">Alertas</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab: Detalhes por OS */}
        <TabsContent value="os">
          <Card className="dark:bg-slate-800/50 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-500" />
                Análise por Ordem de Serviço
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Calendário visual do mês */}
              {reportData?.os_detalhes?.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-3">
                    Calendário de OS - {reportData.periodo?.mes_nome} {reportData.periodo?.ano}
                  </p>
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(d => (
                      <div key={d} className="text-center text-xs font-medium text-slate-500 dark:text-slate-400 py-1">{d}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {(() => {
                      const firstDay = new Date(ano, mes - 1, 1).getDay();
                      const daysInMonth = new Date(ano, mes, 0).getDate();
                      const cells = [];
                      
                      // Células vazias antes do primeiro dia
                      for (let i = 0; i < firstDay; i++) {
                        cells.push(<div key={`empty-${i}`} className="h-16" />);
                      }
                      
                      // Dias do mês
                      for (let day = 1; day <= daysInMonth; day++) {
                        const dateStr = `${ano}-${String(mes).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        const osDoDay = reportData.os_detalhes.filter(os => 
                          os.datas_apontamentos?.includes(dateStr) || os.data_evento === dateStr
                        );
                        const isWeekend = (firstDay + day - 1) % 7 === 0 || (firstDay + day - 1) % 7 === 6;
                        
                        cells.push(
                          <div 
                            key={day} 
                            className={`h-16 p-1 border rounded text-xs ${
                              osDoDay.length > 0 
                                ? 'bg-violet-50 dark:bg-violet-900/20 border-violet-300 dark:border-violet-700' 
                                : isWeekend 
                                ? 'bg-slate-50 dark:bg-slate-900/30 border-slate-200 dark:border-slate-700'
                                : 'border-slate-200 dark:border-slate-700'
                            }`}
                          >
                            <div className={`font-medium ${isWeekend ? 'text-slate-400' : 'text-slate-700 dark:text-slate-300'}`}>{day}</div>
                            {osDoDay.length > 0 && (
                              <div className="space-y-0.5 mt-0.5 overflow-hidden">
                                {osDoDay.slice(0, 2).map((os, i) => (
                                  <div key={i} className="text-[10px] truncate text-violet-700 dark:text-violet-400" title={`${os.numero} - ${os.local || ''} - ${os.servico || ''}`}>
                                    <span className="font-medium">{os.numero}</span>
                                    {os.local && <span className="text-violet-500 dark:text-violet-500"> {os.local.substring(0, 8)}</span>}
                                  </div>
                                ))}
                                {osDoDay.length > 2 && <div className="text-[10px] text-violet-500">+{osDoDay.length - 2}</div>}
                              </div>
                            )}
                          </div>
                        );
                      }
                      
                      return cells;
                    })()}
                  </div>
                </div>
              )}
              
              {/* Lista de OS */}
              {reportData?.os_detalhes?.length > 0 ? (
                <div className="space-y-3">
                  {reportData.os_detalhes.map((os) => (
                    <div key={os.numero} className="border dark:border-slate-700 rounded-lg overflow-hidden">
                      {/* Cabeçalho da OS */}
                      <div className="p-3 bg-slate-100 dark:bg-slate-800 border-b dark:border-slate-700">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-lg text-slate-800 dark:text-slate-100">
                                OS {os.numero}
                              </span>
                              {os.local && (
                                <Badge className="bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
                                  {os.local}
                                </Badge>
                              )}
                              {(os.servico || os.service_type) && (
                                <Badge className="bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400 capitalize">
                                  {os.servico || os.service_type}
                                </Badge>
                              )}
                              <Badge className={`text-xs ${getStatusColor(os.status_eficiencia)}`}>
                                {getStatusLabel(os.status_eficiencia)}
                              </Badge>
                            </div>
                            <div className="flex flex-wrap gap-3 mt-1 text-sm text-slate-600 dark:text-slate-400">
                              {os.data_evento && (
                                <span className="flex items-center gap-1">
                                  <span className="text-slate-400">Data:</span> 
                                  <span className="font-medium">
                                    {new Date(os.data_evento + 'T12:00:00').toLocaleDateString('pt-BR')}
                                  </span>
                                </span>
                              )}
                              {/* Indicador de Relação de Equipamentos */}
                              <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                                os.tem_relacao_equipamentos 
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                              }`}>
                                {os.tem_relacao_equipamentos ? '✓ Rel. Equip.' : '✗ Sem Rel.'}
                              </span>
                            </div>
                          </div>
                          
                          {/* Observações da OS */}
                          {os.observacoes && os.observacoes.length > 0 && (
                            <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded text-xs text-amber-800 dark:text-amber-300">
                              <span className="font-medium">Obs:</span> {os.observacoes.join(' | ')}
                            </div>
                          )}
                          
                          {/* Resumo de horas */}
                          <div className="flex gap-4 text-right mt-2">
                            <div>
                              <p className="text-xs text-slate-500 dark:text-slate-400">Previsto</p>
                              <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                                {(os.horas_previstas?.total || 0).toFixed(1)}h
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 dark:text-slate-400">Apontado</p>
                              <p className="text-lg font-bold text-amber-600 dark:text-amber-400">
                                {(os.horas_apontadas?.total || 0).toFixed(1)}h
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 dark:text-slate-400">Diferença</p>
                              <p className={`text-lg font-bold ${
                                (os.diferenca_horas || 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                              }`}>
                                {(os.diferenca_horas || 0) > 0 ? '+' : ''}{(os.diferenca_horas || 0).toFixed(1)}h
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Detalhamento por funcionário com horários */}
                      {os.funcionarios_detalhes && os.funcionarios_detalhes.length > 0 && (
                        <div className="bg-white dark:bg-slate-900/50">
                          <div className="px-3 py-2 border-b dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                            <p className="text-xs font-medium text-slate-600 dark:text-slate-400">
                              Equipe ({os.funcionarios_detalhes.length} pessoas)
                            </p>
                          </div>
                          <div className="divide-y dark:divide-slate-700">
                            {os.funcionarios_detalhes.map((func, idx) => (
                              <div key={idx} className="px-3 py-2 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-slate-800 dark:text-slate-200 text-sm">
                                      {func.nome}
                                    </span>
                                    {func.contract_type === 'diaria' && (
                                      <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-1.5 py-0.5 rounded">
                                        Diária
                                      </span>
                                    )}
                                  </div>
                                  {func.horarios && func.horarios.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-1">
                                      {func.horarios.map((h, hidx) => (
                                        <span 
                                          key={hidx} 
                                          className={`text-xs px-2 py-0.5 rounded inline-flex items-center gap-1 ${
                                            h.is_termo 
                                              ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-700' 
                                              : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                                          }`}
                                          title={h.is_termo ? `Ajuste: ${h.motivo || 'Batida manual'}` : 'Ponto OK'}
                                        >
                                          {h.is_termo ? (
                                            <span className="text-red-500">⚠</span>
                                          ) : (
                                            <span className="text-green-500">✓</span>
                                          )}
                                          {h.data}: {h.entrada} - {h.saida}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="text-right">
                                  {/* Alerta de ajustes */}
                                  {func.total_termos > 0 && (
                                    <div className="text-xs text-red-500 dark:text-red-400 mb-1">
                                      ⚠ {func.total_termos} ajuste{func.total_termos > 1 ? 's' : ''}
                                    </div>
                                  )}
                                  {/* CLT: mostrar horas | Diária: mostrar quantidade de diárias */}
                                  {func.contract_type === 'diaria' ? (
                                    <div>
                                      <span className="text-purple-600 dark:text-purple-400 font-bold">
                                        {func.dias?.length || 1} diária{(func.dias?.length || 1) > 1 ? 's' : ''}
                                      </span>
                                    </div>
                                  ) : (
                                    <div>
                                      <span className="text-amber-600 dark:text-amber-400 font-bold">
                                        {func.horas.toFixed(1)}h
                                      </span>
                                      {func.dias && func.dias.length > 1 && (
                                        <span className="text-xs text-slate-400 ml-1">
                                          ({(func.horas / func.dias.length).toFixed(1)}h/dia)
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Lista simples se não tiver detalhes */}
                      {(!os.funcionarios_detalhes || os.funcionarios_detalhes.length === 0) && os.funcionarios && os.funcionarios.length > 0 && (
                        <div className="px-3 py-2 bg-white dark:bg-slate-900/50">
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Equipe:</p>
                          <div className="flex flex-wrap gap-1">
                            {os.funcionarios.map((nome, idx) => (
                              <span key={idx} className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-slate-700 dark:text-slate-300">
                                {nome}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <Info className="w-12 h-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                  <p>Nenhuma OS com apontamentos no período</p>
                  <p className="text-xs mt-1">Os apontamentos na Escala do Dia precisam ter o número da OS preenchido</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Ranking de Funcionários */}
        <TabsContent value="ranking">
          <Card className="dark:bg-slate-800/50 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                Ranking de Eficiência
                <span className="text-xs font-normal text-slate-500 dark:text-slate-400 ml-2">
                  (Baseado em ajustes - menos ajustes = maior score)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reportData?.ranking_funcionarios?.length > 0 ? (
                <div className="space-y-3">
                  {reportData.ranking_funcionarios.map((func, index) => (
                    <div
                      key={func.human_id}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        func.termos_count === 0
                          ? 'bg-gradient-to-r from-green-50 to-transparent dark:from-green-900/20 border border-green-200 dark:border-green-800' 
                          : func.termos_count <= 2
                          ? 'bg-gradient-to-r from-amber-50 to-transparent dark:from-amber-900/20'
                          : 'bg-gradient-to-r from-red-50 to-transparent dark:from-red-900/20 border border-red-200 dark:border-red-800'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          func.termos_count === 0 ? 'bg-green-500 text-white' :
                          func.termos_count <= 2 ? 'bg-amber-400 text-amber-900' :
                          'bg-red-500 text-white'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800 dark:text-slate-200">
                            {func.human_name}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                            <span>{func.total_dias_trabalhados} dias</span>
                            <span>|</span>
                            <span>{func.os_trabalhadas?.length || 0} OS</span>
                            {func.termos_count > 0 ? (
                              <span className="text-red-600 dark:text-red-400 font-medium">
                                | ⚠ {func.termos_count} termo{func.termos_count > 1 ? 's' : ''}
                              </span>
                            ) : (
                              <span className="text-green-600 dark:text-green-400 font-medium">
                                | ✓ Sem termos
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold ${getScoreColor(func.score_eficiencia)}`}>
                          {func.score_eficiencia}
                        </p>
                        <p className="text-xs text-slate-500">Score</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <Users className="w-12 h-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                  <p>Nenhum funcionário com apontamentos no período</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Termos Assinados */}
        <TabsContent value="termos">
          <Card className="dark:bg-slate-800/50 dark:border-slate-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-500" />
                  Termos Assinados (Apontamentos sem Ponto)
                </CardTitle>
                {termosData?.resumo?.total_termos > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportTermosCsv}
                    className="flex items-center gap-2 text-green-600 border-green-300 hover:bg-green-50 dark:text-green-400 dark:border-green-700 dark:hover:bg-green-900/20"
                    data-testid="export-termos-csv-btn"
                  >
                    <Download className="w-4 h-4" />
                    Exportar CSV
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {termosData?.resumo?.total_termos > 0 && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-red-700 dark:text-red-400 font-medium">
                      Total: {termosData.resumo.total_termos} termos
                    </span>
                    <span className="text-red-600 dark:text-red-500">
                      {termosData.resumo.total_funcionarios_com_termo} funcionário(s)
                    </span>
                    <span className="text-red-500 dark:text-red-400 text-xs">
                      Média: {termosData.resumo.media_por_funcionario} por pessoa
                    </span>
                  </div>
                </div>
              )}
              
              {termosData?.por_funcionario?.length > 0 ? (
                <div className="space-y-4">
                  {termosData.por_funcionario.map((func) => (
                    <div key={func.human_id} className="border dark:border-slate-700 rounded-lg overflow-hidden">
                      <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border-b dark:border-slate-700 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <XCircle className="w-5 h-5 text-red-500" />
                          <span className="font-medium text-red-800 dark:text-red-300">
                            {func.human_name}
                          </span>
                        </div>
                        <Badge variant="destructive">
                          {func.total_termos} termo{func.total_termos > 1 ? 's' : ''}
                        </Badge>
                      </div>
                      <div className="divide-y dark:divide-slate-700 max-h-64 overflow-y-auto">
                        {func.termos.map((termo, idx) => (
                          <div key={idx} className="px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800/50">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium text-slate-700 dark:text-slate-300">
                                {termo.data ? new Date(termo.data + 'T12:00:00').toLocaleDateString('pt-BR') : '-'}
                              </span>
                              <span className={`px-1.5 py-0.5 rounded text-xs ${
                                termo.tipo === 'entrada' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                              }`}>
                                {termo.tipo === 'entrada' ? 'Entrada' : 'Saída'}: {termo.horario}
                              </span>
                              {termo.os && (
                                <span className="bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded text-xs text-slate-600 dark:text-slate-300">
                                  OS: {termo.os}
                                </span>
                              )}
                              {termo.local && (
                                <span className="bg-violet-100 dark:bg-violet-900/30 px-1.5 py-0.5 rounded text-xs text-violet-700 dark:text-violet-400">
                                  {termo.local}
                                </span>
                              )}
                              {termo.servico && (
                                <span className="bg-cyan-100 dark:bg-cyan-900/30 px-1.5 py-0.5 rounded text-xs text-cyan-700 dark:text-cyan-400 capitalize">
                                  {termo.servico}
                                </span>
                              )}
                            </div>
                            <div className="mt-1 text-xs text-red-600 dark:text-red-400">
                              <span className="font-medium">Motivo:</span> {termo.motivo || 'Não informado'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-green-600 dark:text-green-400">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3" />
                  <p className="font-medium">Nenhum ajuste manual no período!</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Todos os apontamentos foram feitos via ponto
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Alertas */}
        <TabsContent value="alertas">
          <Card className="dark:bg-slate-800/50 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Funcionários em Alerta
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reportData?.funcionarios_alerta?.length > 0 ? (
                <div className="space-y-3">
                  {reportData.funcionarios_alerta.map((func) => (
                    <div
                      key={func.human_id}
                      className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-red-800 dark:text-red-300">
                            {func.human_name}
                          </p>
                          <div className="mt-2 space-y-1 text-xs">
                            {func.divergencias > 0 && (
                              <p className="text-red-600 dark:text-red-400">
                                ⚠ {func.divergencias} divergência(s) de horário
                              </p>
                            )}
                            {func.termos_pendentes > 0 && (
                              <p className="text-amber-600 dark:text-amber-400">
                                📝 {func.termos_pendentes} termo(s) pendente(s)
                              </p>
                            )}
                            {func.dias_falta > 0 && (
                              <p className="text-slate-600 dark:text-slate-400">
                                ❌ {func.dias_falta} falta(s) no período
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${getScoreColor(func.score_eficiencia)}`}>
                            {func.score_eficiencia}
                          </p>
                          <p className="text-xs text-slate-500">Score</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-green-600 dark:text-green-400">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3" />
                  <p className="font-medium">Nenhum funcionário em alerta!</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Todos os colaboradores estão com score acima de 70
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dica de uso */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-800 dark:text-blue-300">Como funciona?</p>
              <p className="text-blue-700 dark:text-blue-400 mt-1">
                1. Cadastre as OS com as horas previstas para cada etapa (montagem, evento, desmontagem).<br />
                2. Na Escala do Dia, vincule os apontamentos à OS correspondente.<br />
                3. O sistema cruza automaticamente e mostra quem otimizou vs quem excedeu as horas.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformancePage;
